import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MapPin, Plus, X, Save } from "lucide-react";

/**
 * Gate obbligatorio per le agenzie che non hanno ancora impostato le zone_operative.
 * Blocca l'accesso al gestionale (montato in App.js) finché non salvano almeno 1 città.
 */
export default function ZoneOperativeGate() {
    const { user } = useAuth();
    const [needsSetup, setNeedsSetup] = useState(false);
    const [zones, setZones] = useState([]);
    const [input, setInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const check = useCallback(async () => {
        if (!user || user.role !== "agenzia") return;
        try {
            const r = await api.get("/profile/zone-operative");
            const z = r.data.zone_operative || [];
            setZones(z);
            setNeedsSetup(z.length === 0);
        } catch { /* silent */ }
    }, [user]);

    useEffect(() => { check(); }, [check]);

    if (!user || user.role !== "agenzia" || !needsSetup) return null;

    const addZone = () => {
        const v = input.trim();
        if (!v) return;
        if (zones.map((z) => z.toLowerCase()).includes(v.toLowerCase())) { setInput(""); return; }
        setZones([...zones, v]);
        setInput("");
    };
    const removeZone = (idx) => setZones(zones.filter((_, i) => i !== idx));

    const submit = async () => {
        if (zones.length === 0) { setErr("Inserisci almeno una città o provincia"); return; }
        setSaving(true); setErr("");
        try {
            await api.put("/profile/zone-operative", { zone_operative: zones });
            setNeedsSetup(false);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="zone-operative-gate" className="fixed inset-0 z-[1150] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="relative w-full max-w-xl border border-gold/40 bg-ink-950 p-8">
                <div className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-2 mb-3">
                    <MapPin size={13} /> Completa il profilo agenzia
                </div>
                <h2 className="font-serif text-2xl text-ink-50 mb-3">Le tue zone operative</h2>
                <p className="text-sm text-ink-300 mb-6">
                    Prima di accedere al gestionale, indica in quali città o province opera la tua agenzia. Riceverai automaticamente i lead di proprietari da queste zone tramite il form pubblico <span className="text-gold">/vendi-casa</span>.
                </p>

                <div className="flex gap-2 mb-3">
                    <input
                        data-testid="zo-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addZone(); } }}
                        placeholder="Es. Milano, Monza, Provincia di Bergamo..."
                        className="flex-1 bg-ink-950 border border-white/15 px-3 py-2 text-sm text-ink-100 focus:border-gold outline-none"
                    />
                    <button data-testid="zo-add" onClick={addZone} className="cta-outline px-3 py-2 text-[11px] flex items-center gap-1.5">
                        <Plus size={12} /> Aggiungi
                    </button>
                </div>

                {zones.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {zones.map((z, i) => (
                            <span key={i} data-testid={`zo-chip-${i}`} className="inline-flex items-center gap-1.5 border border-gold/40 bg-gold/5 text-gold text-[11px] px-2.5 py-1">
                                {z}
                                <button onClick={() => removeZone(i)} className="hover:text-ink-50" data-testid={`zo-remove-${i}`}><X size={11} /></button>
                            </span>
                        ))}
                    </div>
                )}

                {err && <div data-testid="zo-error" className="text-xs text-red-300 border border-red-700/40 bg-red-900/20 px-3 py-2 mb-3">{err}</div>}

                <button
                    onClick={submit}
                    disabled={saving || zones.length === 0}
                    data-testid="zo-submit"
                    className="w-full cta-gold py-3 text-xs flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Save size={13} /> {saving ? "Salvataggio..." : "Salva e continua"}
                </button>
                <p className="text-[10px] text-ink-500 text-center mt-3">Potrai modificare le zone in qualsiasi momento dal profilo agenzia.</p>
            </div>
        </div>
    );
}
